import { useEffect, useRef, useState } from 'react';

/**
 * Tirar foto com o aparelho.
 *
 * Dois caminhos, e a escolha entre eles não é preferência — é qualidade de
 * evidência:
 *
 * **No celular e no tablet**, `<input capture="environment">` abre o
 * aplicativo de câmera do próprio aparelho: foco automático, flash,
 * resolução cheia e EXIF de orientação intacto — que é o que o gerador do
 * PDF usa para não imprimir a foto deitada. Nenhuma câmera desenhada em
 * JavaScript chega perto disso.
 *
 * **No computador**, `capture` é ignorado: o navegador abre o seletor de
 * arquivos e o técnico fica olhando para uma janela de pastas quando pediu
 * uma foto. Aí sim vale `getUserMedia`, com a webcam numa sobreposição.
 *
 * A distinção é feita por `pointer: coarse` — dedo em vez de mouse. É
 * heurística, e por isso **nunca é a única saída**: a sobreposição tem
 * "Escolher arquivo" e, se a câmera falhar ou for negada, cai no seletor
 * de arquivos com a mensagem do erro visível.
 */

const ehTatil = () =>
  typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

const temGetUserMedia = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

/** Nome com data e hora: o arquivo já chega identificável na pasta. */
function nomeDoArquivo(tipo: string): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${tipo}-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`
    + `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.jpg`;
}

// ── Sobreposição da webcam ───────────────────────────────────────────

interface CameraProps {
  tipo: string;
  titulo: string;
  aoTirar(arquivo: File): void;
  aoFechar(): void;
  aoPedirArquivo(): void;
}

export function CameraNaTela({
  tipo, titulo, aoTirar, aoFechar, aoPedirArquivo,
}: CameraProps) {
  const video = useRef<HTMLVideoElement>(null);
  const fluxo = useRef<MediaStream | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pronta, setPronta] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
          audio: false,
        });
        if (!vivo) { s.getTracks().forEach((t) => t.stop()); return; }
        fluxo.current = s;
        if (video.current) {
          video.current.srcObject = s;
          await video.current.play().catch(() => {});
        }
        setPronta(true);
      } catch (e) {
        setErro(e instanceof Error && e.name === 'NotAllowedError'
          ? 'O navegador não liberou a câmera. Escolha um arquivo ou libere o acesso.'
          : 'Não consegui abrir a câmera deste aparelho.');
      }
    })();
    return () => {
      vivo = false;
      fluxo.current?.getTracks().forEach((t) => t.stop());
      fluxo.current = null;
    };
  }, []);

  /**
   * O quadro sai em JPEG a 92 %: é evidência, não miniatura. Reduzir aqui
   * gravaria como prova uma imagem que ninguém fotografou — quem encolhe é
   * o gerador do documento, para o PDF caber num e-mail.
   */
  const disparar = () => {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const tela = document.createElement('canvas');
    tela.width = v.videoWidth;
    tela.height = v.videoHeight;
    tela.getContext('2d')?.drawImage(v, 0, 0);
    tela.toBlob((b) => {
      if (!b) return;
      aoTirar(new File([b], nomeDoArquivo(tipo), { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  return (
    <div className="camera-fundo" role="dialog" aria-modal="true" aria-label={titulo}>
      <div className="camera-caixa">
        <div className="camera-topo">
          <b>{titulo}</b>
          <button type="button" className="botao discreto" onClick={aoFechar}>
            Cancelar
          </button>
        </div>

        {erro ? (
          <div className="aviso erro" role="alert">{erro}</div>
        ) : (
          <video ref={video} className="camera-video" playsInline muted />
        )}

        <div className="camera-acoes">
          <button
            type="button" className="botao secundario"
            onClick={aoPedirArquivo}
          >
            Escolher arquivo
          </button>
          <button
            type="button" className="botao grande"
            disabled={!pronta || !!erro}
            onClick={disparar}
          >
            Tirar foto
          </button>
        </div>
      </div>
    </div>
  );
}

// ── O botão que decide o caminho ─────────────────────────────────────

export interface PedidoDeFoto {
  tipo: string;
  titulo: string;
  /** Índice da amostra na lista; `null` = foto do lote. */
  amostraIndice: number | null;
}

/**
 * Guarda o pedido em curso e sabe abrir o caminho certo.
 *
 * Fica num lugar só para o `<input type="file">` ser um só na página. Um
 * por item do roteiro seriam quatro por amostra — quarenta e oito
 * elementos de arquivo num lote de doze, cada um segurando a referência do
 * seu arquivo.
 */
export function useCaptura(aoReceber: (arquivos: File[], pedido: PedidoDeFoto) => void) {
  const entradaCamera = useRef<HTMLInputElement>(null);
  const entradaArquivo = useRef<HTMLInputElement>(null);
  const pedido = useRef<PedidoDeFoto | null>(null);
  const [naTela, setNaTela] = useState<PedidoDeFoto | null>(null);

  const pedirFoto = (p: PedidoDeFoto) => {
    pedido.current = p;
    if (ehTatil()) {
      // Câmera nativa do aparelho: melhor imagem e EXIF preservado.
      entradaCamera.current?.click();
    } else if (temGetUserMedia()) {
      setNaTela(p);
    } else {
      entradaArquivo.current?.click();
    }
  };

  const pedirArquivo = (p?: PedidoDeFoto) => {
    if (p) pedido.current = p;
    setNaTela(null);
    entradaArquivo.current?.click();
  };

  const receber = (lista: FileList | null) => {
    const p = pedido.current;
    if (!p || !lista?.length) return;
    aoReceber(Array.from(lista), p);
  };

  const entradas = (
    <>
      <input
        ref={entradaCamera} type="file" accept="image/*" capture="environment"
        className="só-leitor"
        onChange={(e) => { receber(e.target.files); e.target.value = ''; }}
      />
      <input
        ref={entradaArquivo} type="file" accept="image/*" multiple
        className="só-leitor"
        onChange={(e) => { receber(e.target.files); e.target.value = ''; }}
      />
    </>
  );

  const sobreposicao = naTela && (
    <CameraNaTela
      tipo={naTela.tipo}
      titulo={naTela.titulo}
      aoFechar={() => setNaTela(null)}
      aoPedirArquivo={() => pedirArquivo()}
      aoTirar={(arquivo) => {
        setNaTela(null);
        if (pedido.current) aoReceber([arquivo], pedido.current);
      }}
    />
  );

  return { pedirFoto, pedirArquivo, entradas, sobreposicao };
}
